import type { Arrow, ImageDefect, ImageScenario } from './imageTypes';
import { SEVERITY_WEIGHT, falsePositivePenalty } from './scoring';
import type { Verdict } from '../components/ImageViewer';

export interface ImageGrade {
  found: ImageDefect[];
  missed: ImageDefect[];
  /** Decoy arrows the player wrongly accused. */
  falsePositives: Arrow[];
  /** Decoy arrows the player correctly left alone. */
  correctRejects: Arrow[];
  score: number;
  rating: string;
  /** Per-arrow outcome, for painting the reveal over the image. */
  verdicts: Map<string, Verdict>;
}

/**
 * Grade a set of selected arrows.
 *
 * Every arrow is a claim the player either endorses or rejects, so this scores
 * both directions: credit for spotting a real defect, a penalty for accusing a
 * decoy. Leaving a decoy alone is correct but earns nothing -- restraint is
 * expected, not rewarded.
 */
export function gradeImage(
  scenario: ImageScenario,
  selected: ReadonlySet<string>,
): ImageGrade {
  const defectByArrow = new Map<string, ImageDefect>();
  for (const defect of scenario.defects) defectByArrow.set(defect.arrowId, defect);

  const found: ImageDefect[] = [];
  const missed: ImageDefect[] = [];
  const falsePositives: Arrow[] = [];
  const correctRejects: Arrow[] = [];
  const verdicts = new Map<string, Verdict>();

  for (const arrow of scenario.arrows) {
    const defect = defectByArrow.get(arrow.id);
    const isSelected = selected.has(arrow.id);

    if (defect && isSelected) {
      found.push(defect);
      verdicts.set(arrow.id, 'found');
    } else if (defect) {
      missed.push(defect);
      verdicts.set(arrow.id, 'missed');
    } else if (isSelected) {
      falsePositives.push(arrow);
      verdicts.set(arrow.id, 'false-positive');
    } else {
      correctRejects.push(arrow);
      verdicts.set(arrow.id, 'correct-reject');
    }
  }

  const totalWeight = scenario.defects.reduce(
    (sum, d) => sum + SEVERITY_WEIGHT[d.severity],
    0,
  );
  const earnedWeight = found.reduce((sum, d) => sum + SEVERITY_WEIGHT[d.severity], 0);

  const raw =
    totalWeight === 0
      ? 0
      : (earnedWeight / totalWeight) * 100 -
        falsePositives.length * falsePositivePenalty(scenario.defects.length);

  const score = Math.max(0, Math.round(raw));

  return {
    found,
    missed,
    falsePositives,
    correctRejects,
    score,
    rating: rate(score),
    verdicts,
  };
}

function rate(score: number): string {
  if (score >= 90) return 'Board Auditor';
  if (score >= 70) return 'Seasoned Scrum Master';
  if (score >= 50) return 'Promising Junior';
  if (score >= 25) return 'Needs a Retro';
  return 'Ship It Anyway';
}
